import React, { useCallback, useEffect, useState } from "react";
import {
  MenuSelectors,
  UISelectors,
  UserContextSelectors,
  AccountSelectors,
  SettingsSelectors,
  SeoSelectors
} from "./redux/selectors";
import { ThemeProvider } from "styled-components";

import BubbleWrapper from "./components/Bubble";
import { EmptyComponent } from "./components/common/EmptyComponent";
import { FullScreenPopup } from "./components/FullScreenPopup";
import { HeaderChunk } from "./segmentedComponents/Header/loader";
import { LeftAreaPC } from "./components/LeftAreaPC";
import Loadable from "react-loadable";
import { MainViewApp } from "./components/MainViewApp";
import { insertSEOFooterAfterSelector } from "./components/SeoElements/SEOFooterContainer";
import { Channel, Topic } from "./constants";
import { MessageBrokerUtils } from "./utils";
import { Overlay } from "./components/common/Overlay";
import Popup from "./components/Popup/Popup";
import { RightSideDrawer } from "./components/RightSideDrawer";
import Spinner from "./components/common/Spinner";
import { Survey } from "./components/Survey";
import { TestComponent } from "./components/TestComponent";
import Toast from "./components/common/Toast";
import { BiometricsOldChunk } from "./components/Menu/Biometrics.old/";
import { useSelector } from "react-redux";
import { ControlCenterLoader } from "./components/ControlCenter";
import GlobalStyle from "./globalStyles";
import { StyledComponentThemeProvider } from "./styles/themes/themeProvider";
import Drawers from "./components/Drawers/loader";
import { PageContextUtils } from "./utils";
import {
  useDocumentClasses,
  useConfig,
  useUserInteraction,
  useBottomBar
} from "./hooks";
import PokerFullScreenPopup from "./components/FullScreenPopup/PokerFullScreenPopup";
import PokerModalContainer from "./components/ModalContainer/PokerModalContainer";
import { BrandUtils } from "./utils/brandUtils";
import { CSSVarNames } from "./constants";
import { ModalWidget } from "./components/ModalWidget";

export const Debug = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "debug/index" */
      "./components/Debug"
    ),
  render(loaded, props) {
    let Component = loaded.Debug;
    return <Component {...props} />;
  },
  loading: EmptyComponent
});

const HookLoader = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "infra/hook.loader" */
      "./HookLoaderComponent"
    ),
  loading: EmptyComponent
});

export const BottomBarNavigationChunk = Loadable({
  loader: () => {
    return import(
      /* webpackChunkName: "bottom-bar-navigation/bottom-bar-navigation" */
      /* webpackPrefetch: true */
      "./components/BottomBarNavigation/BottomBarNavigation"
    );
  },

  loading: EmptyComponent
});

const { environment } = PageContextUtils.getEnvironmentData();

const debugEnabled = [
  "mirage",
  "dev",
  "local",
  "integration",
  "mirage-integ",
  "mirage-gamma"
].some(env => env.toLowerCase() === environment?.toLowerCase());

const App = () => {
  useConfig();
  const { isDisplayedButtonBar } = useBottomBar();
  const [displayMainArea, setDisplayMainArea] = useState(true);
  const [isDisplayedMainViewApp, setIsDisplayedMainViewApp] = useState(false);
  const isPokerClient = BrandUtils.isPokerClient();
  const isPC = useSelector(UserContextSelectors.isPC);
  const touchIdVisible = useSelector(UISelectors.touchIdVisible);
  const seoFooter = useSelector(SeoSelectors.seoFooter) || "";

  const hamburgerMenuVisible = useSelector(MenuSelectors.hamburgerMenuVisible);
  const oddsMenuVisible = useSelector(MenuSelectors.oddsMenuVisible);
  const languageMenuVisible = useSelector(MenuSelectors.languageMenuVisible);
  const quickLinksInnerMenuVisible = useSelector(
    MenuSelectors.quickLinksInnerMenuVisible
  );
  const isMobileRewardsVisible = useSelector(
    AccountSelectors.rewardDrawerActive
  );
  const myAccountAreaVisible = useSelector(AccountSelectors.drawerActive);
  const isSettingsDrawerActive = useSelector(
    SettingsSelectors.general.isDrawerActive
  );
  const appearanceMenuVisible = useSelector(
    MenuSelectors.appearanceMenuVisible
  );

  useEffect(() => {
    const _bottomNavbarMobileGap = isDisplayedButtonBar ? "7.6rem" : "0rem";
    document.body.style.setProperty(
      CSSVarNames.BottomNavbarMobileGap,
      _bottomNavbarMobileGap
    );
  }, [isDisplayedButtonBar]);

  useUserInteraction();
  useDocumentClasses();

  const TouchIdPage = touchIdVisible && <BiometricsOldChunk />;

  useEffect(() => {
    getOutsideResources();
  }, []);

  useEffect(() => {
    const activatedDrawer =
      hamburgerMenuVisible ||
      oddsMenuVisible ||
      languageMenuVisible ||
      quickLinksInnerMenuVisible ||
      myAccountAreaVisible ||
      isSettingsDrawerActive ||
      appearanceMenuVisible ||
      isMobileRewardsVisible;
    setDisplayMainArea(!activatedDrawer);
  }, [
    hamburgerMenuVisible,
    languageMenuVisible,
    oddsMenuVisible,
    quickLinksInnerMenuVisible,
    myAccountAreaVisible,
    isSettingsDrawerActive,
    appearanceMenuVisible,
    isMobileRewardsVisible
  ]);

  const getOutsideResources = () => {
    const oldParent = document.querySelector("#uc-content-container");
    const newParent = document.querySelector(".uc-content-area");
    if (oldParent && newParent) {
      while (oldParent.childNodes.length > 0) {
        newParent.appendChild(oldParent.childNodes[0]);
      }
      oldParent.remove();
    }
  };

  const onToggleMainViewApp = useCallback(flag => {
    setIsDisplayedMainViewApp(flag);
  }, []);

  useEffect(() => {
    const urlDataSub = MessageBrokerUtils.subscribe({
      channel: Channel.SESSION,
      topic: Topic.clientInitSucceeded,
      callback: () => {
        insertSEOFooterAfterSelector(
          "#uc-spectate-sport-client-view",
          seoFooter
        );
      }
    });

    return () => {
      urlDataSub?.unsubscribe();
    };
  }, []);

  return (
    <ThemeProvider theme={StyledComponentThemeProvider.Provide()}>
      <GlobalStyle />
      <div className="App" id="uaaApp">
        <HookLoader />
        {isPC && <LeftAreaPC />}
        <div
          className={`uc-main-area ${displayMainArea ? "" : "hide-content "}`}
        >
          <HeaderChunk />
          <div className="uc-wrapper-area">
            <div className="uc-content-area">
              <div
                id={"uc-main-view-app-container"}
                className={isDisplayedMainViewApp ? "show-main-view-app" : ""}
              >
                <MainViewApp onToggleMainViewApp={onToggleMainViewApp} />
              </div>
            </div>
            <Spinner />
            <Toast />
            <Survey />
            <FullScreenPopup />
            {isPokerClient && (
              <>
                <PokerFullScreenPopup />
                <PokerModalContainer />
              </>
            )}
            <ControlCenterLoader />
            {TouchIdPage}
            {debugEnabled && <Debug />}
            <TestComponent />
          </div>
        </div>
        <Overlay />
        <RightSideDrawer />
        <BubbleWrapper />
        <Drawers />
        <Popup />
        <ModalWidget />
        {isDisplayedButtonBar && <BottomBarNavigationChunk />}
      </div>
    </ThemeProvider>
  );
};

export default App;
